/**
 * Regras de data digitada (dd/mm/aaaa).
 *
 * O calendario nativo do navegador e lento de navegar, entao os campos de data
 * da plataforma sao digitados. Como o usuario passa a escrever livremente, toda
 * a validacao de calendario tem que ser feita aqui: formato, mes valido, dia que
 * existe naquele mes, ano bissexto, limites de periodo e data de nascimento no
 * futuro.
 */

// Sem 1900 a pessoa erra o ano (ex: 0202) e o formulario aceita
export const ANO_MINIMO = 1900;
export const IDADE_MAXIMA = 120;

const DIAS_POR_MES = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export const ehBissexto = ano => (ano % 4 === 0 && ano % 100 !== 0) || ano % 400 === 0;

export const diasNoMes = (mes, ano) => (mes === 2 && ehBissexto(ano) ? 29 : DIAS_POR_MES[mes - 1]);

// Data local, sem fuso: 'aaaa-mm-dd' no construtor do Date seria lido como UTC
// e voltaria um dia
const criarData = (dia, mes, ano) => new Date(ano, mes - 1, dia, 0, 0, 0, 0);

const soDia = data => {
    const d = new Date(data);
    d.setHours(0, 0, 0, 0);
    return d;
};

/** Aplica a mascara dd/mm/aaaa conforme a pessoa digita */
export const mascaraData = texto => {
    const numeros = String(texto || '').replace(/\D/g, '').slice(0, 8);

    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 4) return `${numeros.slice(0, 2)}/${numeros.slice(2)}`;
    return `${numeros.slice(0, 2)}/${numeros.slice(2, 4)}/${numeros.slice(4)}`;
};

/** 'aaaa-mm-dd' (ou Date) -> 'dd/mm/aaaa' */
export const paraTextoBR = valor => {
    if (!valor) return '';

    if (valor instanceof Date) {
        if (Number.isNaN(valor.getTime())) return '';
        const dia = String(valor.getDate()).padStart(2, '0');
        const mes = String(valor.getMonth() + 1).padStart(2, '0');
        return `${dia}/${mes}/${valor.getFullYear()}`;
    }

    const texto = String(valor);
    const iso = texto.split('T')[0];
    const partes = iso.split('-');
    if (partes.length === 3) return `${partes[2]}/${partes[1]}/${partes[0]}`;

    return mascaraData(texto);
};

/** 'dd/mm/aaaa' -> 'aaaa-mm-dd' (sem validar) */
export const paraISO = textoBR => {
    const numeros = String(textoBR || '').replace(/\D/g, '');
    if (numeros.length !== 8) return '';

    return `${numeros.slice(4)}-${numeros.slice(2, 4)}-${numeros.slice(0, 2)}`;
};

/**
 * Valida a data digitada.
 *
 * opcoes:
 *   obrigatorio  - vazio vira erro
 *   naoFuturo    - nao aceita data depois de hoje (nascimento)
 *   idadeMaxima  - limite de anos para tras (nascimento)
 *   minima/maxima- Date ou 'aaaa-mm-dd' com o intervalo permitido
 *   rotuloMinima/rotuloMaxima - texto amigavel no erro do intervalo
 *
 * Retorna { valida, erro, data, iso }.
 */
export const validarData = (textoBR, opcoes = {}) => {
    const {
        obrigatorio = false,
        naoFuturo = false,
        idadeMaxima = IDADE_MAXIMA,
        minima,
        maxima,
        rotuloMinima,
        rotuloMaxima,
    } = opcoes;

    const numeros = String(textoBR || '').replace(/\D/g, '');

    if (!numeros.length) {
        return obrigatorio
            ? { valida: false, erro: 'Informe a data', data: null, iso: '' }
            : { valida: true, erro: '', data: null, iso: '' };
    }

    if (numeros.length < 8) {
        return { valida: false, erro: 'Data incompleta (dd/mm/aaaa)', data: null, iso: '' };
    }

    const dia = Number(numeros.slice(0, 2));
    const mes = Number(numeros.slice(2, 4));
    const ano = Number(numeros.slice(4));

    if (mes < 1 || mes > 12) {
        return { valida: false, erro: 'Mês inválido', data: null, iso: '' };
    }
    if (dia < 1) {
        return { valida: false, erro: 'Dia inválido', data: null, iso: '' };
    }

    const limiteDoMes = diasNoMes(mes, ano);
    if (dia > limiteDoMes) {
        const erro = mes === 2 && dia === 29
            ? `${ano} não é ano bissexto`
            : `Este mês tem ${limiteDoMes} dias`;
        return { valida: false, erro, data: null, iso: '' };
    }

    if (ano < ANO_MINIMO) {
        return { valida: false, erro: `Ano deve ser a partir de ${ANO_MINIMO}`, data: null, iso: '' };
    }

    const data = criarData(dia, mes, ano);
    const hoje = soDia(new Date());
    const iso = `${String(ano).padStart(4, '0')}-${numeros.slice(2, 4)}-${numeros.slice(0, 2)}`;

    if (naoFuturo && data > hoje) {
        return { valida: false, erro: 'A data não pode ser no futuro', data: null, iso: '' };
    }

    if (naoFuturo && idadeMaxima) {
        const maisAntiga = new Date(hoje.getFullYear() - idadeMaxima, hoje.getMonth(), hoje.getDate());
        if (data < maisAntiga) {
            return { valida: false, erro: `Data anterior a ${idadeMaxima} anos atrás`, data: null, iso: '' };
        }
    }

    if (minima) {
        const limite = soDia(typeof minima === 'string' ? criarDataDeISO(minima) : minima);
        if (data < limite) {
            return {
                valida: false,
                erro: rotuloMinima || `A data deve ser a partir de ${paraTextoBR(limite)}`,
                data: null,
                iso: '',
            };
        }
    }

    if (maxima) {
        const limite = soDia(typeof maxima === 'string' ? criarDataDeISO(maxima) : maxima);
        if (data > limite) {
            return {
                valida: false,
                erro: rotuloMaxima || `A data deve ser até ${paraTextoBR(limite)}`,
                data: null,
                iso: '',
            };
        }
    }

    return { valida: true, erro: '', data, iso };
};

/** 'aaaa-mm-dd' -> Date local */
export const criarDataDeISO = iso => {
    const partes = String(iso || '').split('T')[0].split('-');
    if (partes.length !== 3) return new Date(iso);

    return criarData(Number(partes[2]), Number(partes[1]), Number(partes[0]));
};
